import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import Flatpickr from 'flatpickr'
import { get } from 'lodash'
import classNames from 'classnames'
import moment from 'moment'

import ErrorMessage from './ErrorMessage'
import { useFormContext } from '@context/FormContext'

import 'flatpickr/dist/flatpickr.min.css'

const DatePicker = ({ name, autocomplete = 'off', type = 'single', ...props }) => {
  const { values, errors, submitCount, setFieldValue, setFieldTouched } = useFormContext()
  let inputEl = React.createRef()
  let flatpickr

  const momentLocale = moment.localeData()

  const fieldValue = get(values, name)
  const fieldError = get(errors, name)
  const fieldTouched = submitCount > 0
  const errorAndTouched = fieldError && fieldTouched
  const validAndTouched = !fieldError && fieldTouched

  const handleChange = (newVal) => {
    if (!newVal || newVal.length === 0) {
      setFieldValue(name, undefined)
    } else {
      setFieldValue(
        name,
        type === 'single' ? newVal[0] : newVal
      )
    }
  }

  const handleClose = () => {
    setFieldTouched(name, true)
  }
  useEffect(() => {
    if (inputEl.current) {
      flatpickr = new Flatpickr(inputEl.current, {
        altInput: true,
        defaultDate: fieldValue,
        disableMobile: true,
        disabled: props.disabledDates,
        enabled: props.enabledDates,
        formatDate: (date) => moment(date).format('L'),
        maxDate: props.disabledAfter,
        minDate: props.disabledBefore,
        mode: type,
        onChange: handleChange,
        onClose: handleClose,
        // prettier-ignore
        locale: momentLocale._abbr === 'en'
          ? require('flatpickr/dist/l10n/default.js').default
          : require(`flatpickr/dist/l10n/${momentLocale._abbr}.js`).default[momentLocale._abbr]
      })
      
      return () => {
        if (flatpickr) {
          flatpickr.destroy()
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  
  // Update flatpickr when fieldValue changes
  useEffect(() => {
    if (flatpickr && fieldValue) {
      flatpickr.setDate(fieldValue, false)
    }
  }, [fieldValue])

  return (
    <div
      className={classNames(
        'mb-10 relative last:mb-0',
        { 'opacity-60': props.disabled },
        { 'border-danger': errorAndTouched },
        { 'border-success': validAndTouched }
      )}
    >
      {props.label && (
        <label className="block mb-2 select-none md:mx-6" htmlFor={name}>
          {props.label}
        </label>
      )}

      <input
        autoComplete={autocomplete}
        autoFocus={props.autofocus}
        className="bg-gray-100 rounded-lg border border-gray-300 cursor-pointer block py-2 px-12 w-full transition-all duration-200 hover:border-gray-400 focus:border-success focus:ring-2 focus:ring-success/30 disabled:bg-gray-300 disabled:border-gray-300 disabled:cursor-not-allowed"
        disabled={props.disabled}
        id={name}
        name={name}
        placeholder={props.placeholder}
        ref={inputEl}
        type="text"
        value={fieldValue || ''}
      />

      <ErrorMessage render={errorAndTouched} message={fieldError} />
    </div>
  )
}

// prettier-ignore
DatePicker.propTypes = {
  name: PropTypes.string.isRequired,
  autocomplete: PropTypes.string,
  autofocus: PropTypes.bool,
  disabled: PropTypes.bool,
  disabledAfter: PropTypes.instanceOf(Date),
  disabledBefore: PropTypes.instanceOf(Date),
  disabledDates: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  enabledDates: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.oneOf(['multiple', 'range', 'single']).isRequired,
}

export default DatePicker
